import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  SafeAreaView,
  ScrollView,
  Alert,
  ActivityIndicator,
  Linking,
  Dimensions,
  Platform,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../contexts/ThemeContext';
import Header from '../components/Header';
import {
  updateDocumentTitle,
  updateMetaTag,
  updateCanonicalLink,
  generatePageTitle,
  generateCanonicalUrl
} from '../utils/seoUtils';
import Footer from '../components/Footer';

const { width } = Dimensions.get('window');
const isTablet = width >= 768;

// API URL - use relative path for both local and production
const API_URL = '';

const ContactScreen: React.FC = () => {
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successAnimation] = useState(new Animated.Value(0));
  const scrollY = useRef(new Animated.Value(0)).current;

  // SEO setup for contact page
  React.useEffect(() => {
    const title = generatePageTitle('Contact Us');
    const description = 'Get in touch with BC STEM Opportunities. Have questions about STEM opportunities? Want to collaborate? Contact us for support with your STEM journey in British Columbia.';

    updateDocumentTitle(title);
    updateMetaTag('description', description);
    updateCanonicalLink(generateCanonicalUrl('/contact'));

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', generateCanonicalUrl('/contact'), true);
  }, []);

  const showSuccessAnimation = () => {
    setIsSuccess(true);

    // Smooth bounce-in animation
    Animated.sequence([
      Animated.spring(successAnimation, {
        toValue: 1,
        friction: 8,
        tension: 100,
        useNativeDriver: true,
      }),
      Animated.delay(3500), // Show success message longer
      Animated.timing(successAnimation, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setIsSuccess(false);
    });
  };

  const handleInstagramPress = () => {
    const instagramUrl = 'https://instagram.com/bcstem.ca';
    Linking.openURL(instagramUrl).catch(() =>
      Alert.alert('Error', 'Could not open Instagram')
    );
  };

  const handleEmailPress = () => {
    const emailUrl = 'mailto:nonit.gupta@bcstem.ca';
    Linking.openURL(emailUrl).catch(() =>
      Alert.alert('Error', 'Could not open email client')
    );
  };

  const handleSubmit = async () => {
    console.log('🚀 Submit button clicked!');

    // Enhanced validation for all required fields
    if (!name.trim()) {
      Alert.alert('Name Required', 'Please enter your full name');
      return;
    }
    if (name.trim().length < 2) {
      Alert.alert('Invalid Name', 'Please enter at least 2 characters for your name');
      return;
    }
    if (!email.trim()) {
      Alert.alert('Email Required', 'Please enter your email address');
      return;
    }
    if (!email.includes('@') || email.trim().length < 5) {
      Alert.alert('Invalid Email', 'Please enter a valid email address');
      return;
    }
    if (!subject.trim()) {
      Alert.alert('Subject Required', 'Please enter a subject for your message');
      return;
    }
    if (subject.trim().length < 3) {
      Alert.alert('Invalid Subject', 'Please enter at least 3 characters for the subject');
      return;
    }
    if (!message.trim()) {
      Alert.alert('Message Required', 'Please enter your message');
      return;
    }
    if (message.trim().length < 10) {
      Alert.alert('Message Too Short', 'Please provide a more detailed message (at least 10 characters)');
      return;
    }

    try {
      setIsSubmitting(true);
      console.log('📤 Sending email to:', API_URL);
      console.log('📋 Form data:', { name: name.trim(), email: email.trim(), subject: subject.trim() });

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
        }),
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server error:', errorText);
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Success response:', result);

      // Clear form
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');

      // Show success animation
      showSuccessAnimation();

    } catch (error) {
      console.error('❌ Error sending email:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      Alert.alert(
        'Error',
        `Failed to send your message: ${errorMessage}\n\nPlease try again or email us directly at nonit.gupta@bcstem.ca`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = getStyles(theme);

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar style={theme === 'dark' ? 'light' : 'dark'} />
      <Header scrollY={scrollY} backgroundColor={theme === 'dark' ? '#1E1E23' : '#FFFFFF'} />



      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
        scrollEventThrottle={16}
      >
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <View style={styles.heroContent}>
            <View style={styles.iconContainer}>
              <Ionicons
                name="mail"
                size={isTablet ? 32 : 24}
                color={theme === 'dark' ? '#60A5FA' : '#3B82F6'}
              />
            </View>
            <Text
              accessibilityRole="header"
              aria-level={1}
              style={styles.heroTitle}
            >
              Get in Touch
            </Text>
            <Text style={styles.heroSubtitle}>
              Have questions about STEM opportunities? Want to collaborate with us?
              We'd love to hear from you and help you on your STEM journey.
            </Text>
          </View>
        </View>

        {/* Main Content */}
        <View style={styles.mainContent}>
          {/* Main Content Grid */}
          <View style={styles.contentGrid}>
            {/* Left Column - Contact Form */}
            <View style={styles.formColumn}>
              <View style={styles.formSection}>
                <View style={styles.formHeader}>
                  <Text style={styles.sectionTitle}>Send us a Message</Text>
                  <Text style={styles.sectionSubtitle}>
                    We'd love to hear from you. Drop us a message and we'll respond as soon as possible.
                  </Text>
                </View>

                <View style={styles.form}>
                  {/* Success Message within Form */}
                  {isSuccess && (
                    <Animated.View
                      style={[
                        styles.successMessage,
                        {
                          opacity: successAnimation,
                          transform: [
                            {
                              translateY: successAnimation.interpolate({
                                inputRange: [0, 1],
                                outputRange: [20, 0],
                              }),
                            },
                            {
                              scale: successAnimation.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0.95, 1],
                              }),
                            },
                          ],
                        },
                      ]}
                    >
                      <View style={styles.successContent}>
                        <View style={styles.successIconContainer}>
                          <Ionicons name="checkmark-circle" size={32} color="#10B981" />
                        </View>
                        <View style={styles.successTextContainer}>
                          <Text style={styles.successTitle}>Message Sent Successfully!</Text>
                          <Text style={styles.successSubtitle}>
                            Thank you for contacting us. We've sent you a confirmation email and will respond within 24 hours.
                          </Text>
                        </View>
                      </View>
                    </Animated.View>
                  )}

                  <View style={styles.formRow}>
                    <View style={[styles.formField, isTablet && styles.formFieldHalf]}>
                      <Text style={styles.inputLabel}>
                        Full Name <Text style={styles.requiredAsterisk}>*</Text>
                      </Text>
                      <TextInput
                        style={styles.textInput}
                        value={name}
                        onChangeText={setName}
                        placeholder="Enter your full name"
                        placeholderTextColor={theme === 'dark' ? '#71717A' : '#A1A1AA'}
                        autoCapitalize="words"
                      />
                    </View>

                    <View style={[styles.formField, isTablet && styles.formFieldHalf]}>
                      <Text style={styles.inputLabel}>
                        Email Address <Text style={styles.requiredAsterisk}>*</Text>
                      </Text>
                      <TextInput
                        style={styles.textInput}
                        value={email}
                        onChangeText={setEmail}
                        placeholder="your.email@example.com"
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoComplete="email"
                        placeholderTextColor={theme === 'dark' ? '#71717A' : '#A1A1AA'}
                      />
                    </View>
                  </View>

                  <View style={styles.formField}>
                    <Text style={styles.inputLabel}>
                      Subject <Text style={styles.requiredAsterisk}>*</Text>
                    </Text>
                    <TextInput
                      style={styles.textInput}
                      value={subject}
                      onChangeText={setSubject}
                      placeholder="What would you like to discuss?"
                      autoCapitalize="sentences"
                      placeholderTextColor={theme === 'dark' ? '#71717A' : '#A1A1AA'}
                    />
                  </View>

                  <View style={styles.formField}>
                    <Text style={styles.inputLabel}>
                      Message <Text style={styles.requiredAsterisk}>*</Text>
                    </Text>
                    <TextInput
                      style={styles.textArea}
                      value={message}
                      onChangeText={setMessage}
                      placeholder="Tell us more about your inquiry, questions, or how we can help you..."
                      multiline
                      numberOfLines={isTablet ? 6 : 5}
                      textAlignVertical="top"
                      autoCapitalize="sentences"
                      placeholderTextColor={theme === 'dark' ? '#71717A' : '#A1A1AA'}
                    />
                  </View>

                  <TouchableOpacity
                    style={[styles.submitButton, isSubmitting && styles.submitButtonDisabled]}
                    onPress={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <>
                        <Ionicons name="send" size={18} color="#fff" />
                        <Text style={styles.submitButtonText}>Send Message</Text>
                      </>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            {/* Right Column - Contact Methods & Info */}
            <View style={styles.sidebarColumn}>
              {/* Contact Methods */}
              <View style={styles.contactMethodsSection}>
                <Text style={styles.sidebarTitle}>Get in Touch</Text>
                <Text style={styles.sidebarSubtitle}>
                  Prefer a different way to connect? Here are some alternatives.
                </Text>

                <View style={styles.contactMethodsGrid}>
                  <TouchableOpacity
                    style={styles.contactMethod}
                    onPress={handleEmailPress}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.contactMethodIcon, styles.emailIcon]}>
                      <Ionicons
                        name="mail-outline"
                        size={18}
                        color={theme === 'dark' ? '#60A5FA' : '#3B82F6'}
                      />
                    </View>
                    <View style={styles.contactMethodContent}>
                      <Text style={styles.contactMethodTitle}>Email</Text>
                      <Text style={styles.contactMethodText}>nonit.gupta@bcstem.ca</Text>
                    </View>
                    <Ionicons
                      name="chevron-forward"
                      size={16}
                      color={theme === 'dark' ? '#71717A' : '#A1A1AA'}
                    />
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.contactMethod}
                    onPress={handleInstagramPress}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.contactMethodIcon, styles.instagramIcon]}>
                      <Ionicons
                        name="logo-instagram"
                        size={18}
                        color="#E4405F"
                      />
                    </View>
                    <View style={styles.contactMethodContent}>
                      <Text style={styles.contactMethodTitle}>Instagram</Text>
                      <Text style={styles.contactMethodText}>@bcstem.ca</Text>
                    </View>
                    <Ionicons
                      name="chevron-forward"
                      size={16}
                      color={theme === 'dark' ? '#71717A' : '#A1A1AA'}
                    />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Response Info */}
              <Text style={styles.sidebarTitle}>What to Expect</Text>
              <View style={styles.responseInfo}>
                <View style={styles.responseCard}>
                  <View style={styles.responseHeader}>
                    <View style={styles.responseIconContainer}>
                      <Ionicons
                        name="time-outline"
                        size={16}
                        color={theme === 'dark' ? '#10B981' : '#059669'}
                      />
                    </View>
                    <Text style={styles.responseTitle}>Quick Response</Text>
                  </View>
                  <Text style={styles.responseText}>
                    We typically respond within 24 hours during business days.
                  </Text>
                </View>

                <View style={styles.responseCard}>
                  <View style={styles.responseHeader}>
                    <View style={styles.responseIconContainer}>
                      <Ionicons
                        name="shield-checkmark-outline"
                        size={16}
                        color={theme === 'dark' ? '#8B5CF6' : '#7C3AED'}
                      />
                    </View>
                    <Text style={styles.responseTitle}>Secure & Private</Text>
                  </View>
                  <Text style={styles.responseText}>
                    Your information is protected and only used to respond to your inquiry.
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
        <Footer />
      </ScrollView>
    </SafeAreaView>
  );
};

const getStyles = (theme: 'light' | 'dark') =>
  StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: theme === 'dark' ? '#18181B' : '#FFFFFF',
    },
    container: {
      flex: 1,
    },
    contentContainer: {
      flexGrow: 1,
    },
    heroSection: {
      paddingVertical: isTablet ? 60 : 40,
      paddingHorizontal: 24,
      backgroundColor: theme === 'dark' ? '#1E1E23' : '#FFFFFF',
      alignItems: 'center',
    },
    heroContent: {
      maxWidth: 600,
      alignItems: 'center',
    },
    iconContainer: {
      width: isTablet ? 64 : 48,
      height: isTablet ? 64 : 48,
      borderRadius: isTablet ? 32 : 24,
      backgroundColor: theme === 'dark' ? '#232327' : '#DBEAFE',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 16,
      shadowColor: theme === 'dark' ? '#000' : '#3B82F6',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: theme === 'dark' ? 0.3 : 0.15,
      shadowRadius: 8,
      elevation: 4,
    },
    heroTitle: {
      fontSize: isTablet ? 36 : 28,
      fontWeight: '800',
      color: theme === 'dark' ? '#F2F2F2' : '#1E293B',
      textAlign: 'center',
      marginBottom: 12,
      fontFamily: 'Inter-ExtraBold',
    },
    heroSubtitle: {
      fontSize: isTablet ? 18 : 16,
      color: theme === 'dark' ? '#C2C2C2' : '#64748B',
      textAlign: 'center',
      lineHeight: isTablet ? 28 : 24,
      fontFamily: 'Inter-Regular',
    },
    mainContent: {
      flex: 1,
      paddingHorizontal: 24,
      paddingVertical: isTablet ? 40 : 24,
      maxWidth: isTablet ? 1200 : '100%',
      alignSelf: 'center',
      width: '100%',
      backgroundColor: theme === 'dark' ? '#18181B' : '#FFFFFF',
    },
    contentGrid: {
      flexDirection: isTablet ? 'row' : 'column',
      gap: isTablet ? 48 : 32,
      alignItems: 'flex-start',
    },
    formColumn: {
      flex: isTablet ? 2 : 1,
    },
    formSection: {
      marginBottom: 0,
    },
    formHeader: {
      marginBottom: 32,
    },
    sectionTitle: {
      fontSize: isTablet ? 32 : 28,
      fontWeight: '700',
      color: theme === 'dark' ? '#F2F2F2' : '#1E293B',
      marginBottom: 12,
      fontFamily: 'Inter-Bold',
    },
    sectionSubtitle: {
      fontSize: 18,
      color: theme === 'dark' ? '#A1A1AA' : '#64748B',
      lineHeight: 26,
      fontFamily: 'Inter-Regular',
    },
    form: {
      backgroundColor: theme === 'dark' ? '#232327' : '#FFFFFF',
      borderRadius: 24,
      padding: isTablet ? 40 : 32,
      borderWidth: 1,
      borderColor: theme === 'dark' ? '#34343B' : '#E5E7EB',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: theme === 'dark' ? 0.25 : 0.08,
      shadowRadius: 24,
      elevation: 8,
    },
    formRow: {
      flexDirection: isTablet ? 'row' : 'column',
      gap: isTablet ? 24 : 0,
    },
    formField: {
      marginBottom: 24,
    },
    formFieldHalf: {
      flex: 1,
    },
    inputLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: theme === 'dark' ? '#F2F2F2' : '#1F2937',
      marginBottom: 12,
      fontFamily: 'Inter-SemiBold',
    },
    requiredAsterisk: {
      color: '#EF4444',
      fontSize: 16,
      fontWeight: '600',
    },
    textInput: {
      borderWidth: 1.5,
      borderColor: theme === 'dark' ? '#374151' : '#D1D5DB',
      backgroundColor: theme === 'dark' ? '#18181B' : '#FFFFFF',
      borderRadius: 16,
      padding: 18,
      fontSize: 16,
      color: theme === 'dark' ? '#F2F2F2' : '#1F2937',
      fontFamily: 'Inter-Regular',
      minHeight: 56,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: theme === 'dark' ? 0.1 : 0.04,
      shadowRadius: 4,
      elevation: 2,
    },
    textArea: {
      borderWidth: 1.5,
      borderColor: theme === 'dark' ? '#374151' : '#D1D5DB',
      backgroundColor: theme === 'dark' ? '#18181B' : '#FFFFFF',
      borderRadius: 16,
      padding: 18,
      fontSize: 16,
      color: theme === 'dark' ? '#F2F2F2' : '#1F2937',
      fontFamily: 'Inter-Regular',
      minHeight: isTablet ? 140 : 120,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: theme === 'dark' ? 0.1 : 0.04,
      shadowRadius: 4,
      elevation: 2,
    },
    submitButton: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#3B82F6',
      borderRadius: 16,
      paddingVertical: 18,
      paddingHorizontal: 32,
      marginTop: 8,
      shadowColor: '#3B82F6',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.3,
      shadowRadius: 16,
      elevation: 8,
    },
    submitButtonDisabled: {
      backgroundColor: '#9CA3AF',
      shadowOpacity: 0,
    },
    submitButtonText: {
      fontSize: 16,
      color: '#FFFFFF',
      fontWeight: '600',
      marginLeft: 8,
      fontFamily: 'Inter-SemiBold',
    },
    sidebarColumn: {
      flex: isTablet ? 1 : undefined,
      maxWidth: isTablet ? 400 : '100%',
      paddingLeft: isTablet ? 0 : 0,
      paddingTop: isTablet ? 0 : 32,
    },
    sidebarTitle: {
      fontSize: isTablet ? 24 : 20,
      fontWeight: '700',
      color: theme === 'dark' ? '#F2F2F2' : '#1E293B',
      marginBottom: 12,
      fontFamily: 'Inter-Bold',
    },
    sidebarSubtitle: {
      fontSize: 16,
      color: theme === 'dark' ? '#A1A1AA' : '#64748B',
      marginBottom: 24,
      lineHeight: 24,
      fontFamily: 'Inter-Regular',
    },
    contactMethodsSection: {
      marginBottom: 40,
    },
    contactMethodsGrid: {
      flexDirection: 'column',
      gap: 16,
    },
    contactMethod: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 20,
      backgroundColor: theme === 'dark' ? '#232327' : '#FFFFFF',
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme === 'dark' ? '#374151' : '#E5E7EB',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: theme === 'dark' ? 0.15 : 0.06,
      shadowRadius: 12,
      elevation: 4,
    },
    contactMethodIcon: {
      width: 44,
      height: 44,
      borderRadius: 12,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 16,
    },
    emailIcon: {
      backgroundColor: theme === 'dark' ? 'rgba(96, 165, 250, 0.15)' : 'rgba(59, 130, 246, 0.1)',
    },
    instagramIcon: {
      backgroundColor: theme === 'dark' ? 'rgba(228, 64, 95, 0.15)' : 'rgba(228, 64, 95, 0.1)',
    },
    contactMethodContent: {
      flex: 1,
    },
    contactMethodTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme === 'dark' ? '#F2F2F2' : '#1F2937',
      marginBottom: 4,
      fontFamily: 'Inter-SemiBold',
    },
    contactMethodText: {
      fontSize: 14,
      color: theme === 'dark' ? '#60A5FA' : '#3B82F6',
      fontFamily: 'Inter-Medium',
    },
    responseInfo: {
      flexDirection: 'column',
      gap: 16,
    },
    responseCard: {
      padding: 20,
      backgroundColor: theme === 'dark' ? '#232327' : '#FFFFFF',
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme === 'dark' ? '#374151' : '#E5E7EB',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: theme === 'dark' ? 0.15 : 0.06,
      shadowRadius: 12,
      elevation: 4,
    },
    responseHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
    },
    responseIconContainer: {
      width: 28,
      height: 28,
      borderRadius: 8,
      backgroundColor: theme === 'dark' ? '#18181B' : '#F9FAFB',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    responseTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme === 'dark' ? '#F2F2F2' : '#1F2937',
      fontFamily: 'Inter-SemiBold',
    },
    responseText: {
      fontSize: 14,
      color: theme === 'dark' ? '#A1A1AA' : '#64748B',
      lineHeight: 20,
      fontFamily: 'Inter-Regular',
    },
    successMessage: {
      backgroundColor: theme === 'dark' ? '#1E293B' : '#F0FDF4',
      borderRadius: 16,
      padding: 20,
      marginBottom: 24,
      borderWidth: 1,
      borderColor: theme === 'dark' ? '#10B981' : '#BBF7D0',
      shadowColor: '#10B981',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      elevation: 4,
    },
    successContent: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    successIconContainer: {
      marginRight: 16,
      paddingTop: 2,
    },
    successTextContainer: {
      flex: 1,
    },
    successTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme === 'dark' ? '#F2F2F2' : '#065F46',
      marginBottom: 8,
      fontFamily: 'Inter-Bold',
    },
    successSubtitle: {
      fontSize: 14,
      color: theme === 'dark' ? '#A1A1AA' : '#047857',
      lineHeight: 20,
      fontFamily: 'Inter-Regular',
    },
  });

export default ContactScreen;